import React from 'react';
import { Home, Bath, Square, Phone, Mail, Globe, Check, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ProfileImage from '../property/ProfileImage'; // Importamos el componente actualizado

// Constantes para las URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente para manejar imágenes con respaldo
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  // Construir la URL completa de Cloudflare
  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const DiagonalSplitFlyer = ({ property }) => {
  // Asegurar que siempre tengamos un objeto vendedor válido
  const vendedor = property?.vendedor || {
    nombre: 'KAIROS INMOBILIARIA',
    foto_perfil: null,
    tipo_usuario_id: 1,
    email_verificado: false,
    telefono_verificado: false,
    ubicacion: 'PIURA, PERÚ',
    sobre_mi: '',
    sobre_nosotros: '',
    whatsapp: '',
    telefono: '',
    facebook: '',
    instagram: '',
    nombre_usuario: ''
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  const formatTitle = (title) => {
    const words = title.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + ' ' + word).length <= 15) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return lines;
  };

  // Generate property URL
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property?.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property?.id || 'casa-123'}`;
  };

  const titleLines = formatTitle(property.titulo);

  const tipoUsuario = {
    1: 'Cliente',
    2: 'Vendedor',
    3: 'Constructora',
    4: 'Inmobiliaria'
  }[vendedor.tipo_usuario_id] || 'Usuario';

  // Las imágenes ya vienen con las URLs completas desde el backend
  const propertyImages = property.imagenes || [];

  // Definir características destacadas
  const features = [
    { icon: <Home className="w-4 h-4" />, label: `${property.habitaciones} Dormitorios` },
    { icon: <Bath className="w-4 h-4" />, label: `${property.banos} Baños` },
    { icon: <Square className="w-4 h-4" />, label: `${property.area_total}m²` },
    { icon: <Home className="w-4 h-4" />, label: 'Servicios' },
  ];

  return (
    <div className="max-w-2xl bg-white shadow-lg overflow-hidden rounded-lg">
      {/* Header Logo Section */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <ProfileImage 
              url={vendedor.foto_perfil}
              alt={vendedor.nombre || "Perfil"}
              className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
            />
            {(vendedor.email_verificado && vendedor.telefono_verificado) && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="text-white text-sm font-semibold">
              {vendedor.nombre || "KAIROS INMOBILIARIA"}
            </div>
            <div className="text-xs text-gray-300 flex items-center gap-2">
              {tipoUsuario}
            </div>
          </div>
        </div>
        
        {/* Property Type Badge */}
        <div className="bg-blue-500 text-white px-3 py-1 text-xs rounded-full">
          {property?.operacion === 'alquiler' ? 'En Alquiler' : 'En Venta'}
        </div>
      </div>

      {/* Main Content with Diagonal Split */}
      <div className="relative min-h-[600px]">
        {/* Right Images Section */}
        <div className="absolute inset-0 z-0">
          <div className="h-full grid grid-rows-3 gap-2">
            {propertyImages.slice(0, 3).map((imagen, index) => (
              <div key={index} className="relative overflow-hidden">
                <PropertyImage 
                  url={imagen}
                  alt={`Property view ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Diagonal Overlay */}
        <div className="absolute inset-0 z-10"
             style={{
               background: 'linear-gradient(105deg, rgba(17, 24, 39, 0.92) 0%, rgba(17, 24, 39, 0.92) 54%, transparent 54.5%)'
             }}>
        </div>

        {/* Left Content */}
        <div className="relative z-20 text-white p-8 max-w-[55%]">
          <div>
            <div className="text-blue-400 text-sm flex items-center gap-1 mb-2">
              <MapPin size={14} />
              {property.ubicacion}
            </div>
            
            <div className="mb-6">
              {titleLines.map((line, index) => (
                <div key={index} className="text-4xl font-bold">
                  {line}
                </div>
              ))}
            </div>
            
            <p className="text-sm text-gray-300 mb-6">
              {property.descripcion_corta || 'Alta personalización, calidad de diseño y eficiencia de soporte en un poderoso desarrollo inmobiliario.'}
            </p>

            <div className="mb-8 flex justify-between items-end">
              <div>
                <div className="text-blue-400 text-sm uppercase tracking-wider">Precio desde</div>
                <div className="text-3xl font-bold text-white">
                  S/ {formatPrice(property.precio_soles)}
                </div>
                <div className="text-sm text-gray-300">
                  USD ${formatPrice(property.precio_dolares)}
                </div>
              </div>
              
              {/* QR Code */}
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG value={getPropertyUrl()} size={80} />
              </div>
            </div>

            {/* Features */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-blue-400">
                  Características
                </h3>
                <div className="grid grid-cols-2 gap-y-3">
                  {features.slice(0, 6).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <span className="text-blue-400">{feature.icon}</span>
                      <span>{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Footer */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <span>{property.telefono || vendedor.telefono || '+51 123 456 789'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <span>{property.email || vendedor.email || 'contacto@inkayros.com'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>{property.ubicacion}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagonalSplitFlyer;